import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { BlockDef } from "./block.interfaces";
import { arrayField, createBlock } from "./blockBuilder";
import { useDefaultColors } from "./useDefaultColors";
import {
    ActionsWithSharedBorders,
    type ActionsWithSharedBordersProps,
} from "@/views/blocks/tables/tables";
import { ICON_API_BASE_URL } from "@/apps/page/services/page.service";
import { useFieldConfig } from "./useFieldConfig";
export function useTables() {
    const { t } = useI18n();
    const { colorDefaults } = useDefaultColors();
    const { translations } = useFieldConfig();

    const actionsWithSharedBordersBlock = computed<BlockDef>(() =>
        createBlock<ActionsWithSharedBordersProps>({
            typeId: "ActionsWithSharedBorders",
            type: "tables",
            name: t("page.blocks.tables.actionsWithSharedBorders"),
            component: ActionsWithSharedBorders,
        })
            .withArrayField(
                "actions",
                arrayField(
                    translations.value.cell,
                    "items",
                    {
                        imageSrc: {
                            label: translations.value.image,
                            type: "image",
                        },
                        imageAlt: {
                            label: translations.value.alt,
                            type: "text",
                        },
                        title: {
                            label: translations.value.title,
                            type: "text",
                        },
                        description: {
                            label: translations.value.description,
                            type: "html",
                        },
                        link: {
                            label: translations.value.href,
                            type: "text",
                        },
                    },
                    [
                        {
                            imageSrc: `${ICON_API_BASE_URL}/general/settings-01.svg`,
                            imageAlt: "Smart automation icon",
                            title: "Smart Automation",
                            description:
                                "Automate repetitive tasks with intelligent systems that learn and adapt to your business needs. Save time and reduce errors while boosting productivity.",
                            link: "#",
                        },
                        {
                            imageSrc: `${ICON_API_BASE_URL}/charts/bar-chart-01.svg`,
                            imageAlt: "Analytics icon",
                            title: "Advanced Analytics",
                            description:
                                "Gain valuable insights from your data with powerful analytics tools. Visualize trends and make data-driven decisions to grow your business effectively.",
                            link: "#",
                        },
                        {
                            imageSrc: `${ICON_API_BASE_URL}/development/dataflow-01.svg`,
                            imageAlt: "Integration icon",
                            title: "Seamless Integration",
                            description:
                                "Connect all your existing tools and platforms with our solution. Our API-first approach ensures compatibility and streamlines your workflow.",
                            link: "#",
                        },
                        {
                            imageSrc: `${ICON_API_BASE_URL}/security/shield-01.svg`,
                            imageAlt: "Security icon",
                            title: "Enterprise Security",
                            description:
                                "Protect your data with enterprise-grade security features. Our platform includes encryption, multi-factor authentication, and regular security audits.",
                            link: "#",
                        },
                        {
                            imageSrc: `${ICON_API_BASE_URL}/users/user-check-01.svg`,
                            imageAlt: "Time off request icon",
                            title: "Request Time Off",
                            description:
                                "Easily manage employee time-off requests with our intuitive interface. Track vacation days and absences to maintain optimal staffing levels.",
                            link: "#",
                        },
                        {
                            imageSrc: `${ICON_API_BASE_URL}/finances/bank-note-01`,
                            imageAlt: "Payroll icon",
                            title: "Payroll Management",
                            description:
                                "Streamline payroll processes with automated calculations, tax withholdings, and direct deposits. Ensure accurate and timely payments to employees.",
                            link: "#",
                        },
                    ],
                    {
                        imageSrc: `${ICON_API_BASE_URL}/general/plus.svg`,
                        imageAlt: "Add new item icon",
                        title: "New item",
                        description: "New description",
                        link: "#",
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
            ])
            .build()
    );

    return {
        blocks: computed(() => [actionsWithSharedBordersBlock.value]),
    };
}
